import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import { formatINR } from "@/utils/app-formatters";

import {
  PAYOUT_MODE_OPTIONS,
  defaultPaidOn,
  lastDayOfMonth,
  localTodayYmd,
  payoutFormError,
  type PayoutMode,
} from "./payroll-payout-utils";

function apiMessage(error: unknown): string {
  const err = error as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = err?.response?.data?.message;
  return (Array.isArray(m) ? m.join(", ") : m) || err?.message || "Could not save the payout";
}

type PaidDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the page can reload. */
  onDone: (message: string) => void;
  periodId: string;
  yearMonth: string;
} & (
  | { kind: "line"; lineId: string; employeeName: string; amount: number }
  | { kind: "all"; count: number; amount: number }
);

/** Confirms and records a payout for one payslip, or for every payable pending payslip. */
export function MarkPaidDialog(props: PaidDialogProps) {
  const { open, onClose, onDone, periodId, yearMonth } = props;
  const today = localTodayYmd();
  const [paidOn, setPaidOn] = useState(defaultPaidOn(yearMonth, today));
  const [mode, setMode] = useState<PayoutMode>("BANK_TRANSFER");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const formError = payoutFormError({ yearMonth, paidOn, todayYmd: today, mode, reference });

  const submit = async () => {
    setBusy(true);
    setServerError(null);
    try {
      await callApi({
        location:
          props.kind === "line"
            ? `payrollPeriods/${periodId}/lines/${props.lineId}/mark-paid`
            : `payrollPeriods/${periodId}/mark-all-paid`,
        method: "POST",
        data: { paidOn, paymentMode: mode, paymentReference: reference.trim() || undefined },
      });
      onDone(props.kind === "line" ? "Marked as paid" : `Marked ${props.count} payslips as paid`);
    } catch (error) {
      setServerError(apiMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>
        {props.kind === "line" ? "Mark payslip as paid" : "Mark all pending as paid"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {props.kind === "line"
            ? `${props.employeeName}: ${formatINR(props.amount)}.`
            : `${props.count} payslips, ${formatINR(props.amount)} in total, will be recorded with the same date, mode and reference.`}{" "}
          This records a payment you have already made; no money is moved from here.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            label="Paid on"
            type="date"
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: lastDayOfMonth(yearMonth), max: today }}
            fullWidth
          />
          <TextField
            select
            label="Payment mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as PayoutMode)}
            fullWidth
          >
            {PAYOUT_MODE_OPTIONS.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={
              mode === "CASH" ? "Reference (optional)" : "Reference (UTR / cheque no. / UPI ref)"
            }
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            inputProps={{ maxLength: 120 }}
            fullWidth
          />
          {formError ? <Alert severity="info">{formError}</Alert> : null}
          {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy || formError !== null}>
          {busy ? "Saving…" : "Confirm payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type UnpaidDialogProps = {
  open: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
  periodId: string;
  lineId: string;
  employeeName: string;
};

/** Reverses a recorded payout; a reason is required and stays on the payslip. */
export function MarkUnpaidDialog({
  open,
  onClose,
  onDone,
  periodId,
  lineId,
  employeeName,
}: UnpaidDialogProps) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const valid = reason.trim().length >= 3;

  const submit = async () => {
    setBusy(true);
    setServerError(null);
    try {
      await callApi({
        location: `payrollPeriods/${periodId}/lines/${lineId}/mark-unpaid`,
        method: "POST",
        data: { reason: reason.trim() },
      });
      setReason("");
      onDone("Payout reversed; the payslip is pending again");
    } catch (error) {
      setServerError(apiMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>Reverse payout</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {employeeName}&apos;s payslip will go back to pending. The reason is kept on the payslip.
        </DialogContentText>
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 255 }}
          fullWidth
          autoFocus
        />
        {serverError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {serverError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={submit} disabled={busy || !valid}>
          {busy ? "Saving…" : "Reverse payout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
